import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as chatApi from "@/api/chat.api";
import { mutationMeta } from "@/lib/query-client";

export function useChatRooms(refetchInterval: number | false = false) {
  return useQuery({
    queryKey: ["chat", "rooms"],
    queryFn: () => chatApi.listRooms(),
    refetchInterval,
  });
}

export function useChatRoom(id: string | undefined) {
  return useQuery({
    queryKey: ["chat", "rooms", id],
    queryFn: () => chatApi.getRoom(id!),
    enabled: !!id,
  });
}

export function useRoomMessages(
  roomId: string | undefined,
  params?: { page?: number; per_page?: number },
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: ["chat", "messages", roomId, params],
    queryFn: () => chatApi.listRoomMessages(roomId!, params),
    enabled: !!roomId,
    refetchInterval,
  });
}

export function useCreateRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      room_type: string;
      name?: string;
      patient_id?: string;
      member_ids: string[];
    }) => chatApi.createRoom(data),
    meta: mutationMeta.created("Chat room"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "rooms"] });
    },
  });
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, body }: { roomId: string; body: string }) =>
      chatApi.sendChatMessage(roomId, body),
    meta: mutationMeta.silentSuccess,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["chat", "messages", variables.roomId],
      });
    },
  });
}

export function useMarkRoomRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => chatApi.markRoomRead(roomId),
    meta: mutationMeta.silent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "rooms"] });
    },
  });
}

export function useSendChatMessageWithAttachments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomId,
      body,
      files,
      onProgress,
    }: {
      roomId: string;
      body: string;
      files: File[];
      onProgress?: (percent: number) => void;
    }) => chatApi.sendChatMessageWithAttachments(roomId, body, files, onProgress),
    meta: mutationMeta.silentSuccess,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["chat", "messages", variables.roomId],
      });
      queryClient.invalidateQueries({ queryKey: ["chat", "rooms"] });
    },
  });
}
